import React from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import * as Icons from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Eyebrow, AccentTitle, Icon } from "@/components/ServiceSections";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

/* --------------------------- data-driven tables --------------------------- */

export const MatrixTable = ({ matrix }) => (
  <section
    className={`mx-auto max-w-7xl px-5 sm:px-8 lg:px-16 ${matrix.tight ? "pb-16 sm:pb-24" : "py-16 sm:py-24"}`}
    data-testid={`matrix-${slug(matrix.title.accent || matrix.title.pre)}`}
  >
    <Reveal>
      <Eyebrow>{matrix.eyebrow}</Eyebrow>
      <AccentTitle title={matrix.title} className="mt-5 max-w-3xl text-[1.75rem] leading-[1.2] sm:text-4xl lg:text-[2.6rem] lg:leading-[1.14]" />
      {matrix.copy && <p className="mt-5 max-w-3xl text-base leading-relaxed text-[#DDE3F2] sm:mt-6">{matrix.copy}</p>}
    </Reveal>
    <Reveal delay={0.1}>
      {/* Stacked cards on phones and small tablets, table from lg up */}
      <div className="mt-8 flex flex-col gap-3 sm:mt-12 lg:hidden">
        {matrix.rows.map((r, i) => (
          <div
            key={`m-${r[0]}-${i}`}
            className="rounded-xl border border-white/12 bg-white/[0.03] p-5"
            data-testid={`matrix-card-${slug(String(r[0]))}`}
          >
            <p className="font-display text-base font-bold leading-snug text-white">{r[0]}</p>
            <dl className="mt-4 flex flex-col gap-3">
              {r.slice(1).map((cell, j) => (
                <div key={j}>
                  <dt className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#8E9AEA]">
                    {matrix.columns[j + 1]}
                  </dt>
                  <dd className="mt-1 text-base leading-relaxed text-[#DDE3F2] sm:text-sm">{cell}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
      <div className="mt-12 hidden overflow-hidden rounded-xl border border-white/12 lg:block">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-white/12 bg-white/[0.05]">
              {matrix.columns.map((c) => (
                <th key={c} className="px-5 py-4 text-[11px] font-bold uppercase tracking-[0.18em] text-[#B7BFDD]">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.rows.map((r, i) => (
              <tr
                key={r[0] + i}
                className="border-b border-white/[0.07] transition-colors duration-200 last:border-0 hover:bg-white/[0.03]"
                data-testid={`matrix-row-${slug(String(r[0]))}`}
              >
                {r.map((cell, j) => (
                  <td
                    key={j}
                    className={`px-6 py-4 align-top leading-relaxed ${
                      j === 0 ? "font-semibold text-white" : "text-[#DDE3F2]"
                    }`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {matrix.footnote && <p className="mt-4 text-xs leading-relaxed text-[#A6B0C9]">{matrix.footnote}</p>}
    </Reveal>
  </section>
);

export const SpecGrid = ({ spec }) => (
  <section className="border-y border-white/10 bg-[#0A0E19]" data-testid="spec-grid">
    <div className="mx-auto max-w-7xl px-5 py-16 sm:py-24 sm:px-8 lg:px-16">
      <Reveal>
        <Eyebrow>{spec.eyebrow}</Eyebrow>
        <AccentTitle title={spec.title} className="mt-5 max-w-3xl text-[1.75rem] leading-[1.2] sm:text-4xl lg:text-[2.6rem] lg:leading-[1.14]" />
        {spec.copy && <p className="mt-6 max-w-3xl text-base leading-relaxed text-[#DDE3F2]">{spec.copy}</p>}
      </Reveal>
      <div className="mt-10 sm:mt-14 grid gap-px overflow-hidden rounded-xl border border-white/12 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
        {spec.items.map((it, i) => (
          <Reveal key={it.label} delay={Math.min(i, 6) * 0.05}>
            <div className="h-full bg-[#0B0F1A] p-5 sm:p-7" data-testid={`spec-${slug(it.label)}`}>
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#8E9AEA]">{it.label}</p>
              <p className="mt-3 font-display text-lg font-bold leading-snug text-white">{it.value}</p>
              {it.note && <p className="mt-2 text-[15px] leading-relaxed text-[#A6B0C9] sm:text-sm">{it.note}</p>}
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const PhaseTimeline = ({ timeline }) => (
  <section className="mx-auto max-w-7xl px-5 py-16 sm:py-24 sm:px-8 lg:px-16" data-testid="phase-timeline">
    <Reveal>
      <Eyebrow>{timeline.eyebrow}</Eyebrow>
      <AccentTitle title={timeline.title} className="mt-5 max-w-3xl text-[1.75rem] leading-[1.2] sm:text-4xl lg:text-[2.6rem] lg:leading-[1.14]" />
      {timeline.copy && <p className="mt-6 max-w-3xl text-base leading-relaxed text-[#DDE3F2]">{timeline.copy}</p>}
    </Reveal>
    <div className="relative mt-10 flex flex-col gap-5 border-l border-white/12 pl-6 sm:mt-14 sm:gap-6 sm:pl-12">
      {timeline.phases.map((p, i) => (
        <Reveal key={p.title} delay={Math.min(i, 6) * 0.06}>
          <div className="relative" data-testid={`phase-${slug(p.title)}`}>
            <span className="absolute -left-[2.1rem] top-1.5 flex h-6 w-6 items-center justify-center rounded-full border border-[#8E9AEA]/50 bg-[#0B0F1A] text-[10px] font-bold text-[#8E9AEA] sm:-left-[3.6rem]">
              {i + 1}
            </span>
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 transition-colors duration-300 hover:border-[#8E9AEA]/35 sm:p-7">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <h3 className="font-display text-lg font-bold text-white sm:text-xl">{p.title}</h3>
                <span className="rounded-full border border-[#8E9AEA]/35 bg-[#8E9AEA]/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#8E9AEA]">
                  {p.duration}
                </span>
              </div>
              <p className="mt-3 text-base leading-relaxed text-[#DDE3F2] sm:text-sm">{p.copy}</p>
              {p.outputs && (
                <p className="mt-4 text-xs leading-relaxed text-[#A6B0C9]">
                  <span className="font-bold uppercase tracking-wider text-[#B7BFDD]">Output: </span>
                  {p.outputs}
                </p>
              )}
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ------------------------------- packaging -------------------------------- */

export const PricingTiers = ({ pricing }) => (
  <section className="border-y border-white/10 bg-[#0E1526]" data-testid="pricing-tiers">
    <div className="mx-auto max-w-7xl px-5 py-16 sm:py-24 sm:px-8 lg:px-16">
      <Reveal>
        <Eyebrow>{pricing.eyebrow}</Eyebrow>
        <AccentTitle title={pricing.title} className="mt-5 max-w-3xl text-[1.75rem] leading-[1.2] sm:text-4xl lg:text-[2.6rem] lg:leading-[1.14]" />
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-[#DDE3F2]">{pricing.copy}</p>
      </Reveal>
      <div className="mt-10 sm:mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {pricing.tiers.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.08}>
            <div
              className={`relative flex h-full flex-col rounded-2xl border p-6 transition-colors duration-300 sm:p-8 ${
                t.featured
                  ? "border-[#8E9AEA]/55 bg-[#8E9AEA]/[0.08]"
                  : "border-white/12 bg-[#0B0F1A]/70 hover:border-white/25"
              }`}
              data-testid={`tier-${slug(t.name)}`}
            >
              {t.featured && (
                <span className="absolute -top-3 left-8 rounded-full border border-[#8E9AEA]/50 bg-[#0E1526] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#8E9AEA]">
                  Most deployed
                </span>
              )}
              <h3 className="font-display text-2xl font-bold text-white">{t.name}</h3>
              <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#8E9AEA]">{t.for}</p>
              <p className="mt-5 text-base leading-relaxed text-[#DDE3F2] sm:text-sm">{t.copy}</p>
              <ul className="mt-7 flex flex-1 flex-col gap-3">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-base leading-relaxed text-[#DDE3F2] sm:text-sm">
                    <Icons.Check className="mt-0.5 h-4 w-4 shrink-0 text-[#8E9AEA]" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                className={`mt-8 w-full ${t.featured ? "btn-primary" : "btn-secondary"}`}
                data-testid={`tier-cta-${slug(t.name)}`}
              >
                Request pricing
                <Icons.ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <p className="mt-8 max-w-3xl text-[15px] leading-relaxed text-[#A6B0C9] sm:text-sm">{pricing.note}</p>
      </Reveal>
    </div>
  </section>
);

/* ---------------------------- animated SOC visual ------------------------- */

const SOURCES = ["Endpoints", "Servers", "Firewall", "Cloud", "Identity", "Applications"];
const STAGES = [
  { icon: "Database", title: "Collect", copy: "Agents and syslog collectors normalise events into one schema." },
  { icon: "Crosshair", title: "Detect", copy: "Lanvaus rule set + correlation engine, mapped to MITRE ATT&CK." },
  { icon: "Siren", title: "Alert", copy: "Severity-scored alerts with enrichment and on-call notification." },
  { icon: "FolderCheck", title: "Case", copy: "Case created with timeline, evidence and response playbook." },
  { icon: "FileSignature", title: "Report", copy: "CERT-In 6-hour template pre-filled; DPDP evidence retained." },
];

export const SOCPipeline = () => {
  const ref = React.useRef(null);
  const inView = useInView(ref, { margin: "-20%" });
  const [tick, setTick] = React.useState(0);

  React.useEffect(() => {
    if (!inView) return;
    const id = setInterval(() => setTick((t) => t + 1), 2200);
    return () => clearInterval(id);
  }, [inView]);

  const active = tick % STAGES.length;

  return (
    <section ref={ref} className="relative overflow-hidden border-y border-white/10 bg-[#080C16]" data-testid="soc-pipeline">
      <div className="glow-orb left-1/3 top-0 h-[420px] w-[520px] bg-[#4C5A8C]/20" aria-hidden />
      <div className="relative mx-auto max-w-7xl px-5 py-16 sm:py-24 sm:px-8 lg:px-16">
        <Reveal>
          <Eyebrow>How the Product Works</Eyebrow>
          <h2 className="mt-5 max-w-3xl font-display text-[1.75rem] font-bold leading-[1.2] tracking-tight text-white sm:text-4xl sm:leading-tight lg:text-[2.6rem] lg:leading-[1.14]">
            From raw log to a <span className="text-[#8E9AEA]">CERT-In-ready report</span>, in one pipeline.
          </h2>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-[#DDE3F2]">
            Every component below ships pre-configured. You are not handed a toolkit and a wiki: you get a working
            pipeline with our detection content, our compliance mappings and our reporting templates already loaded.
          </p>
        </Reveal>

        <div className="mt-10 sm:mt-14 grid gap-4 lg:grid-cols-[0.85fr_1.15fr]">
          <Reveal>
            <div className="glass-card h-full p-5 sm:p-7">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#8E9AEA]">Ingest sources</p>
              <div className="mt-6 flex flex-col gap-3">
                {SOURCES.map((s, i) => (
                  <div
                    key={s}
                    className="relative flex items-center justify-between overflow-hidden rounded-lg border border-white/10 bg-[#0B0F1A]/70 px-4 py-3"
                    data-testid={`soc-source-${slug(s)}`}
                  >
                    <span className="text-sm text-[#DDE3F2]">{s}</span>
                    <span className="font-mono text-[10px] text-[#7C86A2]">syslog / agent</span>
                    <motion.span
                      className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-transparent via-[#8E9AEA]/25 to-transparent"
                      animate={{ x: ["-4rem", "100%"] }}
                      transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.35, ease: "linear" }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="flex h-full flex-col gap-3">
              {STAGES.map((st, i) => {
                const isActive = i === active;
                return (
                  <div
                    key={st.title}
                    className={`flex items-start gap-4 rounded-xl border px-5 py-4 transition-colors duration-500 sm:gap-5 sm:py-5 ${
                      isActive ? "border-[#8E9AEA]/50 bg-[#8E9AEA]/[0.08]" : "border-white/10 bg-white/[0.02]"
                    }`}
                    data-testid={`soc-stage-${slug(st.title)}`}
                  >
                    <motion.span
                      animate={{ scale: isActive ? 1.08 : 1 }}
                      transition={{ duration: 0.4 }}
                      className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border ${
                        isActive ? "border-[#8E9AEA]/50 bg-[#8E9AEA]/15" : "border-white/10 bg-white/[0.04]"
                      }`}
                    >
                      <Icon name={st.icon} className={`h-5 w-5 ${isActive ? "text-[#8E9AEA]" : "text-[#A6B0C9]"}`} />
                    </motion.span>
                    <div>
                      <p className="font-display text-base font-bold text-white">
                        <span className="mr-2.5 font-mono text-xs text-[#8E9AEA]">0{i + 1}</span>
                        {st.title}
                      </p>
                      <p className="mt-1.5 text-base leading-relaxed text-[#DDE3F2] sm:text-sm">{st.copy}</p>
                    </div>
                    {isActive && (
                      <motion.span
                        className="ml-auto mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#8E9AEA]"
                        animate={{ opacity: [1, 0.2, 1] }}
                        transition={{ duration: 1.2, repeat: Infinity }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------ trust blocks ------------------------------ */

export const Certifications = ({ certifications }) => (
  <section className="mx-auto max-w-7xl px-5 py-16 sm:py-24 sm:px-8 lg:px-16" data-testid="certifications">
    <Reveal>
      <div className="glass-card grid gap-10 p-6 sm:p-10 lg:grid-cols-[1fr_1.15fr] lg:p-12">
        <div>
          <Eyebrow>Credentials</Eyebrow>
          <h2 className="mt-5 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-[2.1rem] lg:leading-[1.2]">
            Certified practitioners, <span className="text-[#B7BFDD]">not a reseller bench.</span>
          </h2>
          <p className="mt-5 text-sm leading-relaxed text-[#DDE3F2] sm:text-base">
            Certifications held across the Lanvaus engineering team. Every engagement is led by a certified
            practitioner, and the person who scopes your work is the person who delivers it.
          </p>
          <Link to="/contact" className="btn-secondary mt-7" data-testid="certifications-cta">
            Ask who will run your engagement
            <Icons.ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {certifications.map((c) => (
            <div
              key={c.short}
              className="rounded-lg border border-white/12 bg-[#0B0F1A]/70 px-5 py-4"
              data-testid={`cert-${slug(c.short)}`}
            >
              <div className="flex items-center gap-2.5">
                <Icons.BadgeCheck className="h-4 w-4 shrink-0 text-[#8E9AEA]" />
                <p className="text-sm font-semibold text-white">{c.short}</p>
              </div>
              <p className="mt-1.5 text-xs leading-relaxed text-[#A6B0C9]">{c.full}</p>
            </div>
          ))}
        </div>
      </div>
    </Reveal>
  </section>
);

export const TrustCommitments = ({ items, eyebrow = "Working With Us", title }) => (
  <section className="border-y border-white/10 bg-[#0D1322]" data-testid="trust-commitments">
    <div className="mx-auto max-w-7xl px-5 py-16 sm:py-24 sm:px-8 lg:px-16">
      <Reveal>
        <Eyebrow>{eyebrow}</Eyebrow>
        <AccentTitle title={title} className="mt-5 max-w-3xl text-[1.75rem] leading-[1.2] sm:text-4xl lg:text-[2.6rem] lg:leading-[1.14]" />
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-[#DDE3F2]">
          The questions every serious buyer asks before signing, answered up front rather than in a procurement
          questionnaire three weeks later.
        </p>
      </Reveal>
      <div className="mt-10 sm:mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {items.map((it, i) => (
          <Reveal key={it.title} delay={Math.min(i, 6) * 0.06}>
            <div className="glass-card h-full p-5 sm:p-7" data-testid={`commitment-${slug(it.title)}`}>
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-white/10 bg-white/[0.05]">
                <Icon name={it.icon} className="h-5 w-5 text-[#8E9AEA]" />
              </span>
              <h3 className="mt-6 font-display text-lg font-bold text-white">{it.title}</h3>
              <p className="mt-3 text-base leading-relaxed text-[#DDE3F2] sm:text-sm">{it.copy}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
